/**
 * CoursePageClarityHome
 *
 * AI Trading Bootcamp sales page built on the Clarity homepage system
 * (ClarityHome.tsx): one dark canvas, hairline rules, pill actions, copy on
 * one side and one real artifact on the other, and native scroll.
 *
 * WHY THIS EXISTS:
 * - It is a candidate treatment for a future PostHog bootcamp experiment. It
 *   is not enrolled anywhere yet; the deterministic review route mounts it
 *   inside a fixed control preview.
 * - Every price, date, cohort name, session, promotion, and challenge fact
 *   comes from the live catalog and session endpoints. When those fail the
 *   page says so and disables purchase. Nothing here invents an offer.
 * - Presentation rules from the V2 annotation round apply: no eyebrows or
 *   kickers, no pills or badges, no generated illustrations, no shine. Real
 *   evidence only: the class recording, the instructor photo, Trustpilot
 *   screenshots, and student video reviews.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  CalendarClock,
  ChevronDown,
  CircleAlert,
  Code2,
  LineChart,
  MessagesSquare,
  Play,
  ScanSearch,
  ShieldCheck,
  Wallet,
} from 'lucide-react';
import { Button } from '../../design-system';
import { useThemeMode } from '../../design-system/theme';
import useUser from '../../hooks/useUser';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';
import useCourseCatalog from '../../hooks/useCourseCatalog';
import useNextPublicChallenge from '../../hooks/useNextPublicChallenge';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import CourseSeo from '../../components/course/CourseSeo';
import CourseLegalDisclaimer from '../../components/course/CourseLegalDisclaimer';
import PreviewVideo from '../../components/course/PreviewVideo';
import StarRating, { TRUSTPILOT_URL } from '../../components/course/StarRating';
import { stripWeekPrefix } from '../../components/course/CurriculumAccordion';
import SpotChatWidget from '../../components/SpotChatWidget';
import ClarityHeroField from '../ClarityHeroField';
import GuaranteeBlock from '../../components/course/GuaranteeBlock';
import PromoCountdown from '../../components/course/PromoCountdown';
import ScrollProgress from '../../components/course/ScrollProgress';
import {
  COMPETITION,
  DELIVERABLES,
  GUARANTEE_DAYS,
  NO_CODING_BADGE,
  PREVIEW_VIDEO_DURATION_SECONDS,
  PREVIEW_VIDEO_ID,
  REVIEW_SHOTS,
  ROB_LINKEDIN_URL,
  ROB_STORY,
  VIDEO_REVIEWS,
} from '../../components/course/courseContent';
import { AI_TRADING_CHALLENGE_NAME } from '../challenges/challengePageUtils';
import '../HomeSurface.css';
import '../ClarityHome.css';
import './CoursePageClarityHome.css';

const LIVE_COURSE_PATH = '/courses/ai-trading-bootcamp';
const SELF_PACED_PATH = '/courses/ai-trading-bootcamp/self-paced';
const CHALLENGES_PATH = '/challenges';
const FIT_CALL_URL = 'https://calendly.com/lumiwealth/botspot-call';
const LIVE_PRODUCT_SLUG = 'ai-bootcamp';
const COURSE_SALES_FLOW = 'course_sales';
const CLASS_PREVIEW_ID = 'class-preview';
const PRICING_ID = 'pricing';
const EASTERN = 'America/New_York';

/** The course-name experiment changes the product name only. */
const COURSE_NAMES = {
  control: { key: 'trading', courseName: 'AI Trading Bootcamp' },
  agentic: { key: 'agentic', courseName: 'Agentic Trading Bootcamp' },
} as const;

type CourseNameVariant = keyof typeof COURSE_NAMES;

type CatalogPlan = {
  planKey: string;
  installments: number;
  display: string | null;
  totalDisplay: string | null;
  originalDisplay: string | null;
  hasDiscount: boolean;
  savingsDisplay: string | null;
};

type CatalogSession = {
  id?: string;
  title?: string;
  subtitle?: string;
  scheduledAt: string;
};

type TuitionWindow = {
  tier: string;
  label: string;
  isCurrent?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  plans: Array<{
    planKey: string;
    installments: number;
    amountCents?: number;
    listAmountCents?: number;
  }>;
};

/** The shape every ANALYTICS_EVENTS descriptor carries. */
type AnalyticsEvent = { name: string };

/** Extra properties an analytics call may attach beside the event itself. */
type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

/**
 * Exposure context from the course-name experiment wrapper. Every field is
 * attached verbatim to analytics events so PostHog can join the exposure.
 */
type ExperimentContext = {
  experiment_name?: string;
  experiment_variant?: string;
  feature_flag?: string;
  is_experiment_active?: boolean;
} & AnalyticsProperties;

type CoursePageClarityHomeProps = {
  variantKey?: CourseNameVariant;
  experimentContext?: ExperimentContext | null;
};

const TOOL_COVERAGE = [
  {
    term: 'Research and code',
    detail: 'Claude, Codex, Cursor, Pine Script, and BotSpot.',
  },
  {
    term: 'Charts and markets',
    detail: 'TradingView, Polymarket, stocks, ETFs, options, and crypto.',
  },
  {
    term: 'Brokers',
    detail:
      'Schwab, Alpaca, Tradier, Interactive Brokers, Coinbase, Bitunix, Kraken, NinjaTrader, and Tradovate.',
  },
];

/**
 * A sentence of comma-separated names, back as the names themselves. Set as a
 * column each one opens a line, so "stocks" mid-sentence becomes "Stocks".
 */
const namesIn = (detail: string): string[] =>
  detail
    .replace(/\.$/, '')
    .split(/,\s*/)
    .map((name) => name.replace(/^and\s+/i, '').trim())
    .filter(Boolean)
    .map((name) => name.charAt(0).toUpperCase() + name.slice(1));

const COVERAGE_GROUPS = TOOL_COVERAGE.map((group) => ({
  term: group.term,
  items: namesIn(group.detail),
}));

/**
 * The path a student walks in class, as four scannable steps. This is the
 * workflow the course teaches, not decoration, so it carries no artwork.
 */
const WORKFLOW_STEPS = [
  {
    beat: 'First',
    title: 'Research',
    detail: 'Ask for a price, the news behind it, and the filings, in plain English.',
  },
  {
    beat: 'Then',
    title: 'Scan',
    detail: 'Build a scanner that returns a short list checked against real market data.',
  },
  {
    beat: 'Then',
    title: 'Review a trade',
    detail: 'Prepare the order at your own broker and check every detail before you approve it.',
  },
  {
    beat: 'Finally',
    title: 'Automate',
    detail: 'Backtest it, then let an agent run it on your schedule inside rules you set.',
  },
];

/** The four problems visitors already recognise. Text only, no artwork. */
const PAIN_POINTS = [
  {
    Icon: ScanSearch,
    title: 'Research eats hours before you trade.',
    problem: 'News, filings, tickers. Too many tabs, no clear short list.',
    solve:
      'Build a scanner with Claude, Codex, Cursor, or BotSpot. Check its short list against real broker data.',
  },
  {
    Icon: LineChart,
    title: 'ChatGPT gave you a price. Your broker disagreed.',
    problem: 'A confident answer can still use stale prices.',
    solve: 'Get prices, positions, and account value from your broker, then review the AI’s work.',
  },
  {
    Icon: BarChart3,
    title: 'Your AI bot had a green week. Skill or luck?',
    problem: 'A winning week says little about other market conditions.',
    solve: 'Backtest across market conditions and run agent evals before trusting a strategy.',
  },
  {
    Icon: MessagesSquare,
    title: 'You wish you could just tell Claude what to do.',
    problem: 'Research, scan, review. Too many menus and disconnected tools.',
    solve: 'Bring Claude, Codex, Cursor, BotSpot, and your broker into one workflow you control.',
  },
];

/**
 * One row of faces, not two. Six players at 2,674px made the reviews the
 * tallest section on the page; three make the same point in a third of it, and
 * the Trustpilot wall underneath is the volume argument.
 */
const SHOWN_VIDEO_REVIEWS = VIDEO_REVIEWS.slice(0, 3);

/**
 * Rob's story minus the resume paragraph. The plaque beside it already prints
 * the Voyager market cap, the student count, the GitHub stars, and the systems
 * built, so that middle paragraph was the numbers a second time in prose.
 */
const BIO_PARAGRAPHS = [ROB_STORY.paragraphs[0], ROB_STORY.paragraphs[2]].filter(Boolean);

/**
 * Six things, not ten. Ten cards of the same shape ran 1,882px on a phone and
 * several of them said what another one already said: the weekly sessions come
 * with the Pro month, the prompt pack and the code library are both working
 * files, lifetime access is what the library is for, and the guarantee is
 * already a fact in the band and a block beside the price.
 */
const INCLUDED = [
  {
    name: 'Five live sessions with Rob',
    detail: 'Build live each week, ask questions on camera, and bring your own setup for review.',
  },
  {
    name: 'Four hours of private coaching',
    detail:
      'Work one-on-one with Rob on your setup, your account, and the trades you want to make.',
  },
  {
    name: 'Recordings and lifetime access',
    detail:
      'Keep every recording, past class, and future session we add. Retake later cohorts as often as you want.',
  },
  {
    name: 'Working files and algorithm library',
    detail:
      'Keep the class checklists, prompts, and files, plus our strategy library. Read, clone, and change the code.',
  },
  {
    name: 'One free month of BotSpot Pro',
    detail:
      'Research, backtest, and run bots during your included Pro month. Weekly algo sessions come with it.',
  },
  { name: COMPETITION.name, detail: COMPETITION.stackDetail },
];

/** Qualify on purpose. The thesis disqualifier stays first (Rob, September 2026). */
const FIT_YES = [
  'You want AI for real research, market scanning, trading, and automation.',
  'You want to place trades while you learn, not just watch lectures.',
  'You want an AI agent that can trade for you every day, under your control.',
  'You already fight tools like TradingView, thinkorswim, or a broker UI and want less friction.',
];

const FIT_NO = [
  'You think AI is a fad. We are all in on it, and you will hate every week of this.',
  'You want a get-rich-quick black box and guaranteed profits.',
  'You only want to watch videos and never place a trade.',
  'You want fully hands-off set-and-forget with zero reviews or guardrails.',
  'You cannot spare about three hours a week during the cohort.',
];

/** Said out loud so the wrong buyer self-selects out before checkout. */
const NOT_COVERED = [
  'Signals you buy and follow blindly with no review.',
  'Managing your money for you. You stay in command.',
  'A coding bootcamp that starts with syntax instead of trading work.',
];

/** What to have ready before week one. */
const REQUIREMENTS = [
  { title: 'A laptop or desktop', detail: 'Mac, Windows, or Linux.' },
  {
    title: 'About three hours a week',
    detail: 'One live session plus time to practice on your own setup.',
  },
  {
    title: 'Your own broker and account',
    detail: 'You review the workflow and stay in control of every account decision.',
  },
];

const KNOW = [
  {
    Icon: Code2,
    claim: "You don't need to code.",
    proof:
      'You direct Claude and Codex in plain English, and we build the workspace together. Deeper coding paths are optional if you want them later.',
  },
  {
    Icon: CalendarClock,
    claim: 'Plan on about three hours a week.',
    proof:
      'One live session of one to two hours, plus practice on your own setup. Every session is recorded if you miss one.',
  },
  {
    Icon: Wallet,
    claim: 'You trade your own account.',
    proof:
      'Capital stays in your brokerage account. Early trades are reviewed before they go out. Later automation runs inside approval rules, size limits, alerts, logs, and a stop control that you set.',
  },
  {
    Icon: CircleAlert,
    claim: 'You can lose money.',
    proof:
      'Trading involves risk. Backtests show simulated results, not future returns. Nobody here promises profits, and you stay in command of every order.',
  },
];

const FAQS = [
  {
    q: 'Do I need to know how to code?',
    a: 'No. You direct Claude and Codex in plain English, and we build the workspace together. Deeper coding paths are optional if you want them later.',
  },
  {
    q: 'What is agentic trading?',
    a: 'It means giving AI a trading job it can repeat: research the market, scan for a setup, prepare or place a reviewed order, and run again on a schedule inside rules you control.',
  },
  {
    q: 'Will I trade real money in class?',
    a: 'You can use paper or real money. Early trades are reviewed before they go out. Later automation runs inside approval rules, size limits, alerts, logs, and a stop control that you set.',
  },
  {
    q: 'Which markets and asset classes does the bootcamp cover?',
    a: 'Weeks one and two focus on stocks, ETFs, research, and scanners. Week three covers options, crypto and crypto futures with Bitunix, and prediction markets with Polymarket, using Claude with TradingView.',
  },
  {
    q: 'Is this a course about building trading bots?',
    a: 'You start by using AI to research and place reviewed trades. By the end you have an AI agent that can research, decide, and trade every day inside guardrails you set.',
  },
  {
    q: 'When do live sessions run?',
    a: 'Five Wednesday evenings at 7:00 PM Eastern. Each live session is about 1 to 2 hours. Plan on about 3 hours a week total once you include practice outside class. Exact dates are listed in the syllabus. Every session is recorded.',
  },
  {
    q: 'What happens in a live session?',
    a: 'Rob demonstrates the workflow, you build alongside him, and the group reviews real setups and questions.',
  },
  {
    q: 'What if I miss a session?',
    a: 'Every session is recorded and added to your portal. You keep the library, including past classes and future sessions we add.',
  },
  {
    q: 'Do I get lifetime access?',
    a: 'Yes. Join once and you can retake later live cohorts as many times as you want. New material stays in your course library.',
  },
  {
    q: 'What is the trading competition?',
    a: `${COMPETITION.body} ${COMPETITION.points.join(' ')}`,
  },
  {
    q: 'Can I try it before I enroll?',
    a: `Yes. Join the free ${AI_TRADING_CHALLENGE_NAME} first. Bring one trading idea to the live kickoff, keep improving it during the week, and bring whatever you have to the live wrap-up one week later.`,
  },
  {
    q: 'Will the price change before the cohort starts?',
    a: 'Every price on this page comes from the live schedule for the current cohort. When the current price is due to end, Enrollment says the date it ends and what the course costs after it.',
  },
  {
    q: 'What if it is not for me?',
    a: `${GUARANTEE_DAYS}-day money-back guarantee.`,
  },
];

const formatEasternDate = (
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions,
): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { ...options, timeZone: EASTERN });
};

const centsToDisplay = (cents?: number): string | null =>
  Number.isFinite(cents ?? NaN)
    ? `$${Math.round(Number(cents) / 100).toLocaleString('en-US')}`
    : null;

/**
 * One-shot enter fade using the Clarity home motion contract:
 * `[data-enter]` nodes fade in once when scrolled into view, focus reveals
 * the block immediately, and reduced motion shows everything at once.
 */
/* The sticky offer bar is content-driven: it grows when checkout fails and
   again when a phone stacks it. A fixed clearance value covered the legal
   disclaimer, so the page reserves the height the bar actually has. */
function useStickyClearance(
  rootRef: React.RefObject<HTMLElement | null>,
  barRef: React.RefObject<HTMLElement | null>,
  chatRef: React.RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const root = rootRef.current;
    const bar = barRef.current;
    if (!root) return undefined;
    if (!bar) {
      root.style.removeProperty('--bootcamp-sticky-height');
      chatRef.current?.style.setProperty('--bootcamp-sticky-height', '0px');
      return undefined;
    }
    const apply = (): void => {
      const height = `${Math.ceil(bar.offsetHeight)}px`;
      root.style.setProperty('--bootcamp-sticky-height', height);
      chatRef.current?.style.setProperty('--bootcamp-sticky-height', height);
    };
    apply();
    if (!window.ResizeObserver) return undefined;
    const observer = new window.ResizeObserver(apply);
    observer.observe(bar);
    return () => observer.disconnect();
  });
}

function useClarityEnter(rootRef: React.RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !window.IntersectionObserver) return undefined;
    const motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    if (motion?.matches) return undefined;

    // Three entrances, per Rob's September direction: headings arrive
    // horizontally, rows rise with a capped stagger, and media settles with a
    // small scale change. Transforms and opacity only.
    const headings = root.querySelectorAll<HTMLElement>(
      '.clarity-home__chapter > div, .clarity-home__split-copy, .bootcamp-clarity__outcomes h2, .ck-preview h2',
    );
    const media = root.querySelectorAll<HTMLElement>(
      '.clarity-home__learn-photo, .ck-preview-frame, .bootcamp-clarity__slate, .bootcamp-clarity__review-frame, .bootcamp-clarity__plaque',
    );
    const rows = root.querySelectorAll<HTMLElement>(
      '.bootcamp-clarity__reviews-wall > *, .control-home__final-grid > *, .bootcamp-clarity__outcomes-list, .bootcamp-clarity__competition-points, .bootcamp-clarity__schedule-notes, .bootcamp-clarity__terms',
    );
    // Grids and boards arrive as a group with a capped stagger. Every tiled
    // section belongs here: left out, a section simply appeared while the one
    // above and the one below it moved, which reads as a missed frame.
    const groups = root.querySelectorAll<HTMLElement>(
      '.clarity-home__claims-grid, .bootcamp-clarity__steps, .bootcamp-clarity__fit-grid, .bootcamp-clarity__reviews-videos, .bootcamp-clarity__coverage, .bootcamp-clarity__included-grid, .bootcamp-clarity__compare, .bootcamp-clarity__faq-list',
    );
    headings.forEach((node) => node.setAttribute('data-enter', 'heading'));
    media.forEach((node) => node.setAttribute('data-enter', 'media'));
    rows.forEach((node) => node.setAttribute('data-enter', 'row'));
    groups.forEach((group) => {
      group.setAttribute('data-enter', 'row');
      group.setAttribute('data-enter-stagger', '');
    });
    // One entrance per block. A framed video inside a staggered grid matched
    // both lists, so it faded in twice over: once with the grid and once on its
    // own delay, which reads as a stutter. The outer block wins, except where
    // it is only a wrapper around a grid that stitches its own stagger: there
    // the grid wins, or six cards inside it arrive as one slab.
    const tiled = [...groups];
    const nodes: HTMLElement[] = [...headings, ...media, ...rows, ...groups].filter((node) => {
      const nestedInAnother = Boolean(node.parentElement?.closest('[data-enter]'));
      const wrapsTiles = tiled.some((grid) => grid !== node && node.contains(grid));
      if (!nestedInAnother && !wrapsTiles) return true;
      node.removeAttribute('data-enter');
      node.removeAttribute('data-enter-stagger');
      return false;
    });
    root.setAttribute('data-enter-ready', '');
    const reveal = (element: Element): void => {
      element.classList.add('is-in');
      observer.unobserve(element);
    };
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // A fast scroll can carry a block from below the fold to above it
          // without ever producing an intersecting frame. Reveal passed blocks
          // too, or real copy stays invisible for the rest of the visit.
          if (!entry.isIntersecting && entry.boundingClientRect.top >= 0) return;
          reveal(entry.target);
        });
      },
      { rootMargin: '0px 0px -32px 0px', threshold: 0.08 },
    );
    let revealFrame = 0;
    const revealPassed = (): void => {
      revealFrame = 0;
      nodes.forEach((node) => {
        if (node.classList.contains('is-in')) return;
        if (node.getBoundingClientRect().top >= window.innerHeight * 0.92) return;
        reveal(node);
      });
    };
    const handleScroll = (): void => {
      if (revealFrame) return;
      revealFrame = window.requestAnimationFrame(revealPassed);
    };
    const revealFocused = (event: FocusEvent) => {
      if (!(event.target instanceof Element)) return;
      const block = event.target.closest('[data-enter]');
      if (block) reveal(block);
    };
    nodes.forEach((node) => observer.observe(node));
    root.addEventListener('focusin', revealFocused);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      observer.disconnect();
      root.removeEventListener('focusin', revealFocused);
      window.removeEventListener('scroll', handleScroll);
      if (revealFrame) window.cancelAnimationFrame(revealFrame);
      root.removeAttribute('data-enter-ready');
    };
  }, [rootRef]);
}

function ReviewGallery({
  title,
  className,
  children,
  testId,
}: {
  title: string;
  className: string;
  children: React.ReactNode;
  testId?: string;
}) {
  const rail = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ start: true, end: false });
  useEffect(() => {
    const element = rail.current;
    if (!element) return;
    const sync = () =>
      setPosition({
        start: element.scrollLeft <= 2,
        end: element.scrollLeft + element.clientWidth >= element.scrollWidth - 2,
      });
    sync();
    element.addEventListener('scroll', sync, { passive: true });
    const observer = new ResizeObserver(sync);
    observer.observe(element);
    return () => {
      element.removeEventListener('scroll', sync);
      observer.disconnect();
    };
  }, []);
  const move = (direction: number) => {
    const element = rail.current;
    const first = element?.firstElementChild;
    if (!element || !first) return;
    const distance =
      first.getBoundingClientRect().width + parseFloat(getComputedStyle(element).columnGap || '0');
    element.scrollBy({
      left: direction * distance,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };
  return (
    <div className="bootcamp-clarity__gallery">
      <div className="bootcamp-clarity__gallery-heading">
        <h3 className="bootcamp-clarity__reviews-label">{title}</h3>
        {(!position.start || !position.end) && (
          <div className="bootcamp-clarity__gallery-controls" aria-label={`${title} navigation`}>
            <Button
              type="button"
              variant="secondary"
              aria-label={`Previous ${title} review`}
              disabled={position.start}
              onClick={() => move(-1)}
            >
              <ArrowLeft aria-hidden="true" size={18} />
            </Button>
            <Button
              type="button"
              variant="secondary"
              aria-label={`Next ${title} review`}
              disabled={position.end}
              onClick={() => move(1)}
            >
              <ArrowRight aria-hidden="true" size={18} />
            </Button>
          </div>
        )}
      </div>
      <div className={className} ref={rail} data-testid={testId} aria-label={`${title} reviews`}>
        {children}
      </div>
    </div>
  );
}

function ReviewVideo({ review }: { review: { youtubeId: string; name: string; title: string } }) {
  const [playing, setPlaying] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (playing) iframeRef.current?.focus();
  }, [playing]);

  return (
    <figure className="bootcamp-clarity__review-video">
      <div className="bootcamp-clarity__review-frame">
        {playing ? (
          <iframe
            ref={iframeRef}
            title={review.title}
            src={`https://www.youtube-nocookie.com/embed/${review.youtubeId}?autoplay=1&rel=0`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            tabIndex={0}
          />
        ) : (
          <button
            type="button"
            className="bootcamp-clarity__review-poster"
            onClick={() => setPlaying(true)}
            aria-label={`Play ${review.name} video review`}
          >
            <img
              src={`https://i.ytimg.com/vi/${review.youtubeId}/hqdefault.jpg`}
              alt=""
              width="480"
              height="360"
              loading="lazy"
            />
            <span className="bootcamp-clarity__review-play" aria-hidden="true">
              <Play size={22} fill="currentColor" strokeWidth={1.6} />
            </span>
          </button>
        )}
      </div>
      <figcaption>{review.name}</figcaption>
    </figure>
  );
}

/** The one thing a buyer needs from the tuition schedule: how long this price
 *  lasts and what it becomes. A table of enrollment windows asked them to work
 *  that out for themselves. */
function nextPriceChange(windows: TuitionWindow[]): { endsOn: string; nextPrice: string } | null {
  const current = windows.find((window) => window.isCurrent);
  const upcoming = windows.find((window) => !window.isCurrent);
  if (!current?.endDate || !upcoming) return null;
  const upfront = upcoming.plans.find((plan) => plan.installments === 1) || upcoming.plans[0];
  const nextPrice = centsToDisplay(upfront?.amountCents);
  const endsOn = formatEasternDate(current.endDate, { month: 'long', day: 'numeric' });
  if (!nextPrice || !endsOn) return null;
  return { endsOn, nextPrice };
}

export default function CoursePageClarityHome({
  variantKey = 'control',
  experimentContext = null,
}: CoursePageClarityHomeProps): React.ReactElement {
  const [websiteTheme] = useThemeMode();
  const pageRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  // SAFETY: useUser is JavaScript. This names the four identity fields this
  // page reads; nothing else is consumed from the hook.
  const { user } = useUser() as {
    user?: { id?: string; sub?: string; email?: string; name?: string } | null;
  };
  const hero = COURSE_NAMES[variantKey] || COURSE_NAMES.control;
  const {
    startCheckout,
    processing,
    checkoutError,
    setCheckoutError,
    catalogLoading: checkoutCatalogLoading,
    catalogError: checkoutCatalogError,
    refreshCatalog: refreshCheckoutCatalog,
    // Every call passes flowContext explicitly, so the hook needs no default.
  } = useCheckoutRedirect();
  // SAFETY: useCourseCatalog is JavaScript. The assertion below names the exact
  // contract this page renders from; the hook owns these values and never
  // substitutes a fallback offer.
  const {
    isLoading: courseCatalogLoading,
    plans,
    upfrontPlan,
    selfPaced,
    sessions,
    cohortStart,
    cohortName,
    isEnrollable,
    formatSessionDate,
    promoEndsAt,
    promoTierName,
    hasActivePromo,
    tuitionSchedule,
    pricingError: coursePricingError,
    retryCatalog: retryCourseCatalog,
  } = useCourseCatalog() as {
    isLoading: boolean;
    plans: CatalogPlan[];
    upfrontPlan: CatalogPlan | null;
    selfPaced: (CatalogPlan & { productName: string | null }) | null;
    sessions: CatalogSession[];
    cohortStart: Date | null;
    cohortName: string | null;
    isEnrollable: boolean;
    formatSessionDate: (value: string | Date) => string;
    promoEndsAt: Date | null;
    promoTierName: string | null;
    hasActivePromo: boolean;
    tuitionSchedule: { windows?: TuitionWindow[] } | null;
    pricingError: Error | null;
    retryCatalog: () => void;
  };
  const { challenge: nextChallenge, kickoffLabel: nextChallengeKickoff } = useNextPublicChallenge();
  useClarityEnter(pageRef);
  useStickyClearance(pageRef, stickyRef, chatRef);

  // The pain points are one set of four lines the reader can flip between
  // their week and the cohort's, rather than two columns of text to map.
  const [painView, setPainView] = useState<'now' | 'cohort'>('now');

  // Displayed offers and checkout validation must recover together.
  const isLoading = courseCatalogLoading || checkoutCatalogLoading;
  const pricingError = coursePricingError || checkoutCatalogError;
  const [offerRecovered, setOfferRecovered] = useState(false);
  const retryCatalog = useCallback(() => {
    setOfferRecovered(false);
    setCheckoutError(null);
    retryCourseCatalog();
    void refreshCheckoutCatalog();
    setOfferRecovered(true);
  }, [refreshCheckoutCatalog, retryCourseCatalog, setCheckoutError]);

  // Keep the visitor's choice in this history entry so returning from checkout
  // restores it. Only a currently sellable catalog plan can become selected.
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPlanKey = new URLSearchParams(location.search).get('bootcampPlan');
  const [checkoutCtaLocation, setCheckoutCtaLocation] = useState<string | null>(null);
  const sellablePlans = useMemo(() => plans.filter((plan) => Boolean(plan.display)), [plans]);
  const sellableUpfront = upfrontPlan?.display ? upfrontPlan : null;
  // Match the control's installment-first offer; checkout uses the same plan.
  const leadPlan = sellablePlans.reduce<(typeof sellablePlans)[number] | null>(
    (best, plan) => (!best || plan.installments > best.installments ? plan : best),
    null,
  );
  const selectedPlan =
    sellablePlans.find((plan) => plan.planKey === selectedPlanKey) ||
    leadPlan ||
    sellableUpfront ||
    sellablePlans[0] ||
    null;

  const track = useCallback(
    (event: AnalyticsEvent, extra: AnalyticsProperties = {}) => {
      pushEvent({
        event,
        source: window.location.pathname,
        variant: hero.key,
        userId: user?.id || user?.sub,
        email: user?.email,
        ...(experimentContext || {}),
        ...extra,
      });
    },
    [experimentContext, hero.key, user],
  );

  const choosePlan = useCallback(
    (planKey: string) => {
      // Restoring the default or a URL choice is not a visitor selection event.
      const previousPlanKey = selectedPlan?.planKey ?? null;
      if (planKey === previousPlanKey) return;
      track(ANALYTICS_EVENTS.BOOTCAMP_PLAN_SELECTED, { planKey, previousPlanKey });
      const next = new URLSearchParams(location.search);
      next.set('bootcampPlan', planKey);
      navigate(
        { pathname: location.pathname, search: `?${next}`, hash: location.hash },
        { replace: true, state: location.state, preventScrollReset: true },
      );
    },
    [location, navigate, selectedPlan, track],
  );

  // A radiogroup a screen reader announces must behave like one: arrows move
  // the selection, only the selected row is in the tab order.
  const movePlan = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
      if (!keys.includes(event.key) || !sellablePlans.length) return;
      event.preventDefault();
      const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
      const next =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? sellablePlans.length - 1
            : (index + (forward ? 1 : -1) + sellablePlans.length) % sellablePlans.length;
      choosePlan(sellablePlans[next].planKey);
      const group = event.currentTarget.parentElement;
      const target = group?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next];
      target?.focus();
    },
    [choosePlan, sellablePlans],
  );

  const trackVideo = useCallback(
    (event: AnalyticsEvent, ctaLocation: string) => {
      track(event, {
        video_id: PREVIEW_VIDEO_ID,
        video_length_seconds: PREVIEW_VIDEO_DURATION_SECONDS,
        cta_location: ctaLocation,
      });
    },
    [track],
  );

  const trackPath = useCallback(
    (path: string, ctaLocation: string, extra: AnalyticsProperties = {}) => {
      track(ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED, {
        path,
        cta_location: ctaLocation,
        ...extra,
      });
    },
    [track],
  );

  const trackChallenge = useCallback(
    (ctaLocation: string) =>
      trackPath('free_challenge', ctaLocation, {
        challenge_id: nextChallenge?.id || null,
        challenge_slug: nextChallenge?.slug || null,
      }),
    [nextChallenge, trackPath],
  );

  const enroll = useCallback(
    (ctaLocation: string) => {
      setCheckoutCtaLocation(ctaLocation);
      const planKey = selectedPlan?.planKey;
      if (!planKey) return undefined;
      track(ANALYTICS_EVENTS.BOOTCAMP_CHECKOUT_STARTED, {
        cta_location: ctaLocation,
        path: 'live',
        productSlug: LIVE_PRODUCT_SLUG,
        planKey,
      });
      return startCheckout({
        products: [{ productSlug: LIVE_PRODUCT_SLUG, planKey }],
        flowContext: COURSE_SALES_FLOW,
        metadata: {
          origin: {
            flow: COURSE_SALES_FLOW,
            ctaLocation,
            variant: hero.key,
            design: 'clarity_home',
            experimentName: experimentContext?.experiment_name,
            experimentVariant: experimentContext?.experiment_variant,
            returnUrl: '/ai-bot-builder-bootcamp/watch?welcome=1',
          },
        },
        contact: { email: user?.email || '', name: user?.name || '' },
      });
    },
    [experimentContext, hero.key, selectedPlan, startCheckout, track, user],
  );

  const hasUpcomingCohort = useMemo(() => {
    if (!cohortStart) return false;
    const startAt = new Date(cohortStart).getTime();
    return Number.isFinite(startAt) && startAt > Date.now();
  }, [cohortStart]);
  const startLabel = hasUpcomingCohort && cohortStart ? formatSessionDate(cohortStart) : null;

  /**
   * The band under the hero is a strip of one-line facts, so the run of dates
   * is one value: first stop to last. Five separate stops on a rail turned the
   * strip into a two-storey block of columns, and every one of those dates is
   * already printed against its week in the syllabus.
   */
  const sessionRange = useMemo(() => {
    if (!sessions.length) return null;
    const shortDate = (value: string): string =>
      new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'America/New_York',
      });
    const first = shortDate(sessions[0].scheduledAt);
    const last = shortDate(sessions[sessions.length - 1].scheduledAt);
    return first === last ? first : `${first} – ${last}`;
  }, [sessions]);
  const sessionWeekday = sessions.length
    ? formatEasternDate(sessions[0].scheduledAt, { weekday: 'long' })
    : null;
  const sessionTime = sessions.length
    ? new Date(sessions[0].scheduledAt).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: EASTERN,
      })
    : null;

  // Never say "Enrollment unavailable" while catalog/sessions are still loading.
  const enrollmentUnavailable = Boolean(pricingError) || !isEnrollable || !sellablePlans.length;
  const ctaDisabled = isLoading || enrollmentUnavailable || processing || !selectedPlan;
  const ctaLabel = processing
    ? 'Opening checkout'
    : enrollmentUnavailable && !isLoading
      ? 'Enrollment unavailable'
      : 'Enroll now';
  const showOffer = !isLoading && !pricingError && Boolean(selectedPlan);
  const tuitionWindows = tuitionSchedule?.windows || [];
  const priceChange = nextPriceChange(tuitionWindows);

  // The sticky bar only exists once the hero's own action has scrolled away,
  // and only when there is a real offer to act on. It never appears over a
  // broken catalog, where it would promise a purchase the page cannot take.
  const heroActionsRef = useRef<HTMLDivElement>(null);
  const pricingActionRef = useRef<HTMLDivElement>(null);
  const finalActionRef = useRef<HTMLDivElement>(null);
  const [heroActionPassed, setHeroActionPassed] = useState(false);
  const [pricingOnScreen, setPricingOnScreen] = useState(false);
  const [finalActionOnScreen, setFinalActionOnScreen] = useState(false);
  useEffect(() => {
    const heroTarget = heroActionsRef.current;
    const pricingTarget = pricingActionRef.current;
    const finalTarget = finalActionRef.current;
    if (!window.IntersectionObserver) return undefined;
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === heroTarget) {
            setHeroActionPassed(!entry.isIntersecting && entry.boundingClientRect.top < 0);
          }
          if (entry.target === pricingTarget) setPricingOnScreen(entry.intersectionRatio === 1);
          if (entry.target === finalTarget) setFinalActionOnScreen(entry.intersectionRatio === 1);
        });
      },
      { threshold: [0, 1], rootMargin: '-64px 0px 0px 0px' },
    );
    if (heroTarget) observer.observe(heroTarget);
    if (pricingTarget) observer.observe(pricingTarget);
    if (finalTarget) observer.observe(finalTarget);
    return () => observer.disconnect();
  }, []);
  // Hide only when an enrollment action is visible, not when the tall pricing
  // section or its alternative offers enter the viewport.
  // It stays mounted while checkout opens: unmounting the pressed control
  // strips focus and takes its error message with it.
  const showStickyBar =
    heroActionPassed && !pricingOnScreen && !finalActionOnScreen && showOffer && isEnrollable;

  // One live region per offer surface, always mounted: its text changes between
  // checking, failed and recovered, so a recovery is announced instead of the
  // pressed control vanishing and dropping focus to the body.
  const statusMessage = isLoading
    ? 'Checking current pricing and dates.'
    : pricingError
      ? 'Pricing could not load, so enrollment is paused on this page.'
      : offerRecovered
        ? 'Pricing is up to date. Enrollment is open again.'
        : '';

  const enrollmentStatus = (ctaLocation: string) => (
    <div className="bootcamp-clarity__status-group">
      <p
        aria-live="polite"
        className={`bootcamp-clarity__status${pricingError ? ' is-error' : ''}`}
        data-testid={ctaLocation === 'pricing' ? 'pricing-error' : 'offer-status'}
        role="status"
      >
        {statusMessage}
      </p>
      {/* When the offer cannot be shown, the salvage paths are the ones that
          still sell the course: try the catalog again, or talk to Rob. An
          account reserves nothing, so this page does not ask for one. */}
      {pricingError ? (
        <div className="bootcamp-clarity__recovery-actions">
          <Button onClick={retryCatalog} size="sm" type="button" variant="secondary">
            Try again
          </Button>
          <Button
            as="a"
            href={FIT_CALL_URL}
            onClick={() => trackPath('fit_call', ctaLocation)}
            rel="noopener noreferrer"
            size="sm"
            target="_blank"
            variant="secondary"
          >
            Talk to the instructor
          </Button>
        </div>
      ) : null}
      {/* Any reason the action is disabled has to be stated above it, including
          a cohort whose plans carry no price yet. */}
      {!isLoading && !pricingError && enrollmentUnavailable ? (
        <div
          className="bootcamp-clarity__status"
          data-testid={ctaLocation === 'pricing' ? 'enroll-fallback' : undefined}
        >
          <p>
            {isEnrollable
              ? 'Enrollment for the next run is not open yet.'
              : 'Live dates are not open yet.'}
          </p>
          {/* Three things a visitor can still do, as three buttons. Set as
              links inside the sentence they read as an afterthought, and a
              visitor had to find them inside the prose that explains why the
              main action is off. */}
          <div className="bootcamp-clarity__status-actions">
            {selfPaced?.display ? (
              <Button
                as={Link}
                data-testid="hero-self-paced"
                onClick={() => trackPath('self_paced', ctaLocation)}
                size="sm"
                to={SELF_PACED_PATH}
                variant="secondary"
              >
                Start the self-paced archive for {selfPaced.display}
              </Button>
            ) : null}
            <Button
              as="a"
              href={FIT_CALL_URL}
              onClick={() => trackPath('fit_call', ctaLocation)}
              rel="noopener noreferrer"
              size="sm"
              target="_blank"
              variant="secondary"
            >
              Book a call with the instructor
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );

  const checkoutRecovery = (ctaLocation: string) =>
    checkoutError && checkoutCtaLocation === ctaLocation ? (
      <div className="bootcamp-clarity__recovery">
        <p className="bootcamp-clarity__status is-error" role="alert">
          {checkoutError || 'Checkout could not open.'}
        </p>
        <Button onClick={retryCatalog} size="sm" type="button" variant="secondary">
          Refresh pricing
        </Button>
      </div>
    ) : null;

  return (
    <div
      className="control-home clarity-home bootcamp-clarity bsui-scope"
      data-bs-theme={websiteTheme}
      // The fixed bar would otherwise sit on top of the last rows of the page.
      data-sticky-offer={showStickyBar ? 'true' : undefined}
      data-bootcamp-design="clarity-home"
      data-testid="course-page"
      data-variant={hero.key}
      id="course-content"
      ref={pageRef}
      role="main"
      tabIndex={-1}
    >
      <ScrollProgress />
      <CourseSeo path={LIVE_COURSE_PATH} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: { '@type': 'Answer', text: faq.a },
            })),
          })}
        </script>
      </Helmet>

      <section className="control-home__hero clarity-home__hero bootcamp-clarity__hero">
        {/* Same WebGPU filament field as the Clarity home hero: it reacts to the
            pointer, pauses off-screen and when the tab is hidden, stills itself
            under reduced motion, and falls back to a static image without
            WebGPU. Decorative, so it stays out of the reading order. */}
        <ClarityHeroField hostSelector=".bootcamp-clarity__hero" />
        <div className="control-home__container clarity-home__hero-grid">
          <div className="clarity-home__hero-copy">
            <h1 data-testid="hero-course-name">{hero.courseName}</h1>
            <p className="control-home__hero-summary" data-testid="hero-outcome">
              Place your first reviewed trade in week one. Build a research and trading agent that
              follows your rules.
            </p>
            <div className="bootcamp-clarity__enrollment">
              <div className="bootcamp-clarity__hero-purchase">
                {showOffer && selectedPlan ? (
                  <div className="bootcamp-clarity__hero-offer" data-testid="hero-payment-offer">
                    <div className="bootcamp-clarity__hero-price" data-testid="hero-price">
                      {selectedPlan.installments === 1 &&
                      sellableUpfront?.hasDiscount &&
                      sellableUpfront.originalDisplay ? (
                        <s data-testid="hero-was-price">
                          <span className="bootcamp-clarity__sr">Was </span>
                          {sellableUpfront.originalDisplay}
                        </s>
                      ) : null}
                      <strong data-testid="hero-now-price">
                        <span className="bootcamp-clarity__sr">Now </span>
                        {selectedPlan.display}
                      </strong>
                      {selectedPlan.installments > 1 ? <span>per month</span> : null}
                    </div>
                    <div className="bootcamp-clarity__hero-payment-details">
                      {selectedPlan.installments > 1 ? (
                        <span>{`${selectedPlan.installments} monthly payments · ${selectedPlan.totalDisplay} total`}</span>
                      ) : null}
                      <a href={`#${PRICING_ID}`} onClick={() => trackPath('pricing', 'hero')}>
                        Payment options
                      </a>
                    </div>
                  </div>
                ) : (
                  <span className="bootcamp-clarity__hero-unavailable" data-testid="hero-price">
                    Unavailable right now
                  </span>
                )}
                <div className="bootcamp-clarity__hero-cta-column">
                  <div className="control-home__hero-actions" ref={heroActionsRef}>
                    {isLoading || enrollmentUnavailable ? (
                      <Button
                        as="a"
                        data-testid="enroll-hero"
                        href={`#${PRICING_ID}`}
                        iconAfter={<ArrowRight aria-hidden="true" />}
                        onClick={() => trackPath('pricing', 'hero')}
                        size="lg"
                      >
                        See dates and price
                      </Button>
                    ) : (
                      <Button
                        aria-busy={processing || undefined}
                        data-testid="enroll-hero"
                        disabled={processing}
                        iconAfter={<ArrowRight aria-hidden="true" />}
                        onClick={() => enroll('hero')}
                        size="lg"
                        type="button"
                      >
                        {ctaLabel}
                      </Button>
                    )}
                  </div>
                  <a
                    className="bootcamp-clarity__hero-call"
                    data-testid="instructor-hero"
                    href={FIT_CALL_URL}
                    onClick={() => trackPath('fit_call', 'hero')}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Book a call with Rob
                  </a>
                </div>
              </div>
              {checkoutRecovery('hero')}
            </div>
          </div>
          <section
            className="bootcamp-clarity__hero-media"
            aria-label="Class preview"
            data-testid="class-preview"
            id={CLASS_PREVIEW_ID}
          >
            <div className="bootcamp-clarity__screen">
              <PreviewVideo
                heading="Watch a real class before you pay."
                blurb="18 unedited minutes. Follow a real research workflow, then hear the questions students asked."
                onPlay={() => trackVideo(ANALYTICS_EVENTS.BOOTCAMP_VIDEO_STARTED, 'class_preview')}
                onComplete={() =>
                  trackVideo(ANALYTICS_EVENTS.BOOTCAMP_VIDEO_COMPLETED, 'class_preview')
                }
              />
              <div className="bootcamp-clarity__preview-actions" data-testid="preview-actions">
                <a
                  className="bootcamp-clarity__video-fallback"
                  href={`https://www.youtube.com/watch?v=${PREVIEW_VIDEO_ID}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Watch on YouTube
                </a>
              </div>
            </div>
          </section>
        </div>
      </section>

      {/* Schedule and commitment sit below the hero. Price appears once, next
          to the enrollment action, and payment choices live in Pricing. */}
      <section
        className="bootcamp-clarity__facts"
        aria-label="Course commitment"
        data-testid="hero-sub"
      >
        <div className="control-home__container">
          <div className="bootcamp-clarity__terms" data-testid="course-facts">
            <dl className="bootcamp-clarity__terms-deal">
              <div>
                <dt>Format</dt>
                <dd>
                  {sessions.length ? `${sessions.length} live sessions` : 'Five live sessions'} on
                  Zoom
                </dd>
              </div>
              <div>
                <dt>Dates</dt>
                <dd data-testid="facts-dates">
                  {sessionRange ||
                    (startLabel ? `Starts ${startLabel}` : 'Posted when the next cohort opens')}
                </dd>
              </div>
              <div>
                <dt>Guarantee</dt>
                <dd>{GUARANTEE_DAYS}-day money-back guarantee</dd>
              </div>
            </dl>
            {/* Everything that would have made a value wrap. A fact whose value
                runs to two lines leaves the three beside it hanging at the top
                of the row, so the bar holds only what fits on one line and the
                rest closes it as a note. */}
            <p className="bootcamp-clarity__terms-note">
              {sessionWeekday && sessionTime
                ? `${sessionWeekday}s at ${sessionTime} Eastern. `
                : ''}
              About 3 hours a week.{' '}
              <span data-testid="no-coding-reassurance">{NO_CODING_BADGE}</span>.
            </p>
          </div>
        </div>
      </section>

      <section
        className="control-home__section clarity-home__claims bootcamp-clarity__problems"
        aria-labelledby="bootcamp-problems-title"
        data-testid="pain-points"
      >
        <div className="control-home__container">
          <header className="clarity-home__chapter">
            <div>
              <h2 id="bootcamp-problems-title">Does this sound familiar?</h2>
            </div>
          </header>
          {/* A comparison board, not a slideshow. Both states are on the page at
              once in two columns, and the control is the two column headers:
              pressing one widens the column under it and dims the other, so the
              section shows the delta instead of hiding half of it behind a
              click. Hiding one side meant a reader had to hold four lines in
              their head to compare anything. */}
          <div className="bootcamp-clarity__compare" data-view={painView}>
            <div
              className="bootcamp-clarity__swap-toggle"
              role="group"
              aria-label="Compare the two states"
            >
              <span className="bootcamp-clarity__swap-spacer" aria-hidden="true">
                Compare
              </span>
              <button
                aria-pressed={painView === 'now'}
                onClick={() => setPainView('now')}
                type="button"
              >
                Where you are now
              </button>
              <button
                aria-pressed={painView === 'cohort'}
                onClick={() => setPainView('cohort')}
                type="button"
              >
                In the cohort
              </button>
            </div>
            {PAIN_POINTS.map((pain) => (
              <article key={pain.title}>
                <h3>
                  <pain.Icon size={20} strokeWidth={1.5} aria-hidden="true" />
                  <span>{pain.title}</span>
                </h3>
                <div
                  className="bootcamp-clarity__swap-cell"
                  data-state="now"
                  data-active={painView === 'now'}
                >
                  <span className="bootcamp-clarity__swap-tag">Where you are now</span>
                  <p>{pain.problem}</p>
                </div>
                <div
                  className="bootcamp-clarity__swap-cell"
                  data-state="cohort"
                  data-active={painView === 'cohort'}
                >
                  <span className="bootcamp-clarity__swap-tag">In the cohort</span>
                  <p>{pain.solve}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        className="control-home__section bootcamp-clarity__outcomes"
        aria-labelledby="bootcamp-outcomes-title"
        data-testid="what-youll-learn"
      >
        <div className="control-home__container">
          <header className="clarity-home__chapter">
            <div>
              <h2 id="bootcamp-outcomes-title">What you will leave with.</h2>
              <p>Five things you own after the five weeks, on your own machine and account.</p>
            </div>
          </header>
          {/* A manifest, not a checklist: one slab whose rows alternate tone,
              the thing you keep on the left and what it actually means on the
              right, under a header that names the two columns. Five ticked
              lines on bare ground said "list" and nothing else. */}
          <div className="bootcamp-clarity__manifest">
            <p className="bootcamp-clarity__manifest-head" aria-hidden="true">
              <span>You keep</span>
              <span>What that means</span>
            </p>
            <ol aria-label="What you leave with" className="bootcamp-clarity__outcomes-list">
              {DELIVERABLES.map((item: { title: string; detail: string }) => (
                <li key={item.title}>
                  <strong>{item.title}</strong>
                  <span>{item.detail}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section
        className="control-home__section bootcamp-clarity__instructor"
        aria-labelledby="bootcamp-instructor-title"
        data-testid="course-instructor"
      >
        <div className="control-home__container">
          <header className="clarity-home__chapter">
            <div>
              <h2 id="bootcamp-instructor-title">Meet your instructor.</h2>
            </div>
          </header>
          {/* Two objects, not seven separately spaced blocks: a plaque carrying
              his face, the claim he makes about himself and the six counts that
              back it, and beside it the same story in his own voice closing on
              the link that verifies him. The claim now sits directly above its
              receipts, which is the argument. As a band of its own under the
              row, the numbers read as a second section about nobody. */}
          <div className="bootcamp-clarity__who">
            <div className="bootcamp-clarity__plaque">
              <figure className="clarity-home__learn-photo">
                <img
                  alt="Rob Grzesik, instructor of the AI Trading Bootcamp"
                  decoding="async"
                  src="/images/rob.jpg"
                />
              </figure>
              <div className="bootcamp-clarity__plaque-id">
                <p className="bootcamp-clarity__who-name">Rob Grzesik</p>
                <p className="bootcamp-clarity__who-role">
                  CEO of BotSpot · Creator of Lumibot · Master of Finance, Schulich.
                </p>
              </div>
              <ul className="bootcamp-clarity__credentials" aria-label="Instructor credentials">
                {ROB_STORY.credentials.map((credential: { value: string; label: string }) => (
                  <li key={credential.label}>
                    <strong>{credential.value}</strong>
                    <span>{credential.label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bootcamp-clarity__who-copy">
              <blockquote className="bootcamp-clarity__mentor-quote">
                “So I stopped guessing. I taught myself to backtest everything.”
              </blockquote>
              <p className="bootcamp-clarity__mentor-intro">
                Rob built technology handling $3 billion in transactions at Greystone and was an
                early engineer at Voyager Digital. Now he teaches you the research, testing, and
                review process behind an AI trading workflow you can run yourself.
              </p>
              <details className="bootcamp-clarity__mentor-story">
                <summary>Read Rob's story</summary>
                <div>
                  {BIO_PARAGRAPHS.map((paragraph: string) => (
                    <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                  ))}
                </div>
              </details>
              <Button
                as="a"
                href={ROB_LINKEDIN_URL}
                rel="noopener noreferrer"
                size="sm"
                target="_blank"
                variant="secondary"
              >
                View LinkedIn profile
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section
        className="control-home__section bootcamp-clarity__workflow"
        aria-labelledby="bootcamp-workflow-title"
        data-testid="course-workflow"
      >
        <div className="control-home__container">
          <details className="bootcamp-clarity__workflow-details">
            <summary>How the research-to-trade workflow works</summary>
            <div className="bootcamp-clarity__workflow-panel">
              <header className="clarity-home__chapter">
                <div>
                  <h2 id="bootcamp-workflow-title">From an idea to a reviewed trade.</h2>
                  <p>
                    The same four moves every week, on your own account. Week one ends with a trade
                    you reviewed and approved yourself.
                  </p>
                </div>
              </header>
              {/* Research, scan, and approval lead to the final automation step.
              Keep the semantic sequence identical as the layout changes. */}
              <ol className="bootcamp-clarity__steps">
                {WORKFLOW_STEPS.map((step) => (
                  <li key={step.title}>
                    <span className="bootcamp-clarity__flow-beat">{step.beat}</span>
                    <strong>{step.title}</strong>
                    <p>{step.detail}</p>
                  </li>
                ))}
              </ol>
            </div>{' '}
          </details>
        </div>
      </section>

      <section
        className="control-home__section bootcamp-clarity__syllabus"
        aria-labelledby="bootcamp-syllabus-title"
        data-testid="curriculum"
        id="curriculum"
      >
        {/* Summary on the left, the outline on the right: the shape every course
            page worth copying uses, and the one that keeps the week rows in a
            single readable column instead of a two-column accordion. */}
        <div className="control-home__container bootcamp-clarity__syllabus-layout">
          <header className="clarity-home__chapter">
            <div>
              <h2 id="bootcamp-syllabus-title">
                {sessions.length
                  ? 'What you build each week.'
                  : 'From first trade to working agent.'}
              </h2>
              <p>
                Start with a reviewed trade. Finish with an AI agent that runs on your schedule,
                within rules you set.
              </p>
              {sessions.length ? (
                <p>
                  {sessionWeekday && sessionTime
                    ? `Live on ${sessionWeekday}s at ${sessionTime} Eastern`
                    : 'Live on Zoom'}
                  . One to two hours on Zoom. Every session is recorded.
                </p>
              ) : null}
            </div>
          </header>
          <aside
            className="clarity-home__learn-slate bootcamp-clarity__slate"
            aria-label={sessions.length ? 'Weekly syllabus' : 'Live session schedule'}
          >
            <header>
              <strong>
                {sessions.length ? cohortName || hero.courseName : 'Live session schedule'}
              </strong>
              <span>
                {sessions.length
                  ? `${sessions.length} live sessions`
                  : startLabel
                    ? `Starts ${startLabel}`
                    : 'Live cohort'}
              </span>
            </header>
            {sessions.length ? (
              <ol aria-label="Weekly sessions" className="bootcamp-clarity__weeks">
                {sessions.map((session, index) => (
                  <li key={session.id || index}>
                    <details className="bootcamp-clarity__week" open={index === 0}>
                      <summary>
                        <span className="bootcamp-clarity__week-meta">
                          Week {index + 1} · {formatSessionDate(session.scheduledAt)}
                        </span>
                        <strong>{stripWeekPrefix(session.title || `Week ${index + 1}`)}</strong>
                      </summary>
                      {session.subtitle ? <p>{session.subtitle}</p> : null}
                    </details>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="bootcamp-clarity__slate-empty">
                <p data-testid="curriculum-unavailable" role="status">
                  {pricingError
                    ? 'Session details could not load. Please try again.'
                    : isLoading
                      ? 'Loading session details.'
                      : startLabel
                        ? 'Session times and the week-by-week outline will appear when published. Every session is recorded.'
                        : 'Live dates and session details will appear here when the next cohort opens.'}
                </p>
                {pricingError ? (
                  <Button onClick={retryCatalog} size="sm" type="button" variant="secondary">
                    Reload session details
                  </Button>
                ) : null}
              </div>
            )}
          </aside>
        </div>
        <div className="control-home__container">
          {/* By this point the reader has watched a real session, met the
              instructor and read every week, so the first ask for money is
              honest here. It stays secondary; the offer itself is below. */}
          <div className="bootcamp-clarity__cta-band">
            <p>Bring your own setup. Build it with Rob.</p>
            <div className="bootcamp-clarity__cta-band-actions">
              <Button
                as="a"
                data-testid="preview-curriculum"
                href={`#${PRICING_ID}`}
                iconAfter={<ArrowRight aria-hidden="true" />}
                onClick={() => trackPath('pricing', 'after_curriculum')}
                variant="secondary"
              >
                See dates and price
              </Button>
              <Button
                as="a"
                data-testid="instructor-after-curriculum"
                href={FIT_CALL_URL}
                onClick={() => trackPath('fit_call', 'after_curriculum')}
                rel="noopener noreferrer"
                target="_blank"
                variant="ghost"
              >
                Talk to the instructor
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What the five weeks actually put in a buyer's hands. This used to be
          two lines under the outcomes; it is the answer to "will this cover my
          broker, my market, my tools", so it gets a section. */}
      <section
        className="control-home__section bootcamp-clarity__skills"
        aria-labelledby="bootcamp-skills-title"
        data-testid="course-skills"
      >
        <div className="control-home__container bootcamp-clarity__skills-layout">
          <header className="clarity-home__chapter">
            <div>
              <h2 id="bootcamp-skills-title">Works with your setup.</h2>
              <p>Bring your tools and broker. No new platform to learn first.</p>
            </div>
          </header>
          <dl className="bootcamp-clarity__coverage" data-testid="tool-coverage">
            {COVERAGE_GROUPS.map((group) => (
              <div key={group.term}>
                <dt>{group.term}</dt>
                <dd>
                  <ul>
                    {group.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section
        className="control-home__section bootcamp-clarity__included"
        aria-labelledby="bootcamp-included-title"
        data-testid="course-included"
      >
        <div className="control-home__container">
          <header className="clarity-home__chapter">
            <div>
              <h2 id="bootcamp-included-title">Everything included.</h2>
              <p>
                Build together in class. Get private time with Rob. Keep the tools and recordings.
              </p>
            </div>
          </header>
          {/* Live teaching leads; the resources students keep follow it. */}
          <aside
            className="clarity-home__learn-slate bootcamp-clarity__slate"
            aria-label="What is included"
          >
            <a className="bootcamp-clarity__teaching-preview" href={`#${CLASS_PREVIEW_ID}`}>
              <img
                src={`https://i.ytimg.com/vi/${PREVIEW_VIDEO_ID}/maxresdefault.jpg`}
                alt=""
                width="1280"
                height="720"
                loading="lazy"
              />
              <span>
                Preview a real class <ArrowRight size={16} aria-hidden="true" />
              </span>
            </a>
            {[INCLUDED.slice(0, 2), INCLUDED.slice(2)].map((items, group) => (
              <ul
                key={group}
                className={`bootcamp-clarity__included-grid bootcamp-clarity__benefits bootcamp-clarity__benefits--${group === 0 ? 'teaching' : 'resources'}`}
              >
                {items.map((item) => (
                  <li
                    key={item.name}
                    data-testid={item.name === COMPETITION.name ? 'trading-competition' : undefined}
                  >
                    <strong>{item.name}</strong>
                    <span>{item.detail}</span>
                  </li>
                ))}
              </ul>
            ))}
          </aside>
        </div>
      </section>

      {/* Reviews are the highest-converting asset we have, so they get the most
          room on the page and they close the opening act: the recording shows
          the class, the instructor says who runs it, and the students say what
          came of it. All three land before the page argues anything. */}
      <section
        className="control-home__section bootcamp-clarity__reviews"
        aria-labelledby="bootcamp-reviews-title"
        data-testid="student-reviews"
      >
        <div className="control-home__container">
          <header className="clarity-home__chapter">
            <div>
              <h2 id="bootcamp-reviews-title">Student reviews.</h2>
              <p>
                Lumiwealth students, in their own words. Watch their videos, read the original
                screenshots, or see more reviews on Trustpilot.
              </p>
              <StarRating rating={4.6} linkWholeRating />
            </div>
          </header>
          <ReviewGallery
            title="On camera"
            className="bootcamp-clarity__reviews-videos"
            testId="video-reviews"
          >
            {SHOWN_VIDEO_REVIEWS.map(
              (review: { youtubeId: string; name: string; title: string }) => (
                <ReviewVideo key={review.youtubeId} review={review} />
              ),
            )}
          </ReviewGallery>
          <ReviewGallery title="On Trustpilot" className="bootcamp-clarity__reviews-wall">
            {REVIEW_SHOTS.map(
              (shot: { src: string; alt: string; tilt: number; width: number; height: number }) => (
                <a
                  key={shot.src}
                  href={shot.src}
                  aria-label={`Enlarge ${shot.alt}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  // SAFETY: a CSS custom property is a valid style entry that
                  // React's CSSProperties type does not model.
                  style={{ '--bootcamp-clarity-tilt': `${shot.tilt}deg` } as React.CSSProperties}
                >
                  <img
                    src={shot.src}
                    alt={shot.alt}
                    width={shot.width}
                    height={shot.height}
                    loading="lazy"
                  />
                </a>
              ),
            )}
          </ReviewGallery>
          <Button
            as="a"
            href={TRUSTPILOT_URL}
            rel="noopener noreferrer"
            target="_blank"
            variant="secondary"
          >
            Read all reviews on Trustpilot
          </Button>
        </div>
      </section>

      <section
        className="control-home__section bootcamp-clarity__fit"
        aria-labelledby="bootcamp-fit-title"
        data-testid="fit-section"
      >
        <div className="control-home__container">
          <header className="clarity-home__chapter">
            <div>
              <h2 id="bootcamp-fit-title">Is this for you?</h2>
            </div>
          </header>
          <details className="bootcamp-clarity__fit-details">
            <summary>Who gets the most from this bootcamp?</summary>
            <div className="bootcamp-clarity__fit-grid">
              <div>
                <h3>This is for you if</h3>
                <ul>
                  {FIT_YES.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3>This is not for you if</h3>
                <ul>
                  {FIT_NO.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </details>
          <div className="bootcamp-clarity__fit-extra">
            <div data-testid="need-block">
              <h3>What you need</h3>
              <ul>
                {REQUIREMENTS.map((item) => (
                  <li key={item.title}>
                    <strong>{item.title}</strong> {item.detail}
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="not-covered">
              <h3>What this is not</h3>
              <ul>
                {NOT_COVERED.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section
        className="control-home__section bootcamp-clarity__pricing"
        aria-labelledby="bootcamp-pricing-title"
        data-testid="pricing"
        id={PRICING_ID}
      >
        <div className="control-home__container bootcamp-clarity__pricing-layout">
          <div className="clarity-home__split-copy bootcamp-clarity__pricing-heading">
            <h2 id="bootcamp-pricing-title">Enroll in the {cohortName || hero.courseName}.</h2>
            <p>{startLabel ? `Starts ${startLabel}. ` : ''}Choose how to pay.</p>
          </div>
          <aside
            className="clarity-home__learn-slate bootcamp-clarity__slate bootcamp-clarity__offer"
            aria-label="Enrollment"
            data-testid={
              pricingError
                ? 'pricing-error-card'
                : !isLoading && !isEnrollable
                  ? 'enrollment-closed-card'
                  : 'pricing-card'
            }
          >
            <div className="bootcamp-clarity__offer-body">
              {enrollmentStatus('pricing')}
              {showOffer ? (
                <div
                  className="bootcamp-clarity__plans"
                  role="radiogroup"
                  aria-label="Payment options"
                >
                  {sellablePlans.map((plan, index) => {
                    const selected = plan.planKey === selectedPlan?.planKey;
                    const isInstallment = plan.installments > 1;
                    return (
                      <button
                        key={plan.planKey}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className="bootcamp-clarity__plan"
                        onClick={() => choosePlan(plan.planKey)}
                        onKeyDown={(event) => movePlan(event, index)}
                        tabIndex={selected ? 0 : -1}
                      >
                        <span className="bootcamp-clarity__plan-name">
                          <span className="bootcamp-clarity__plan-dot" aria-hidden="true" />
                          {isInstallment ? `${plan.installments} monthly payments` : 'Pay in full'}
                        </span>
                        <span className="bootcamp-clarity__plan-amount">
                          {selected && plan.hasDiscount && plan.originalDisplay ? (
                            <s>
                              <span className="bootcamp-clarity__sr">Was </span>
                              {plan.originalDisplay}
                              {isInstallment ? '/mo' : ''}
                            </s>
                          ) : null}
                          <strong>
                            <span className="bootcamp-clarity__sr">Now </span>
                            {plan.display}
                            {isInstallment ? <em>/mo</em> : null}
                          </strong>
                        </span>
                        <span className="bootcamp-clarity__plan-total">
                          {isInstallment ? `${plan.totalDisplay} total` : 'One payment'}
                          {selected && plan.hasDiscount && plan.savingsDisplay
                            ? ` · Save ${plan.savingsDisplay} vs. full price`
                            : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <div className="bootcamp-clarity__offer-actions" ref={pricingActionRef}>
                <Button
                  aria-busy={processing || undefined}
                  data-testid="enroll-pricing"
                  disabled={ctaDisabled}
                  iconAfter={<ArrowRight aria-hidden="true" />}
                  onClick={() => enroll('pricing')}
                  size="lg"
                  type="button"
                >
                  {ctaLabel}
                </Button>
              </div>
              {checkoutRecovery('pricing')}
              {/* The shared block repeats its own headline by default. */}
              <GuaranteeBlock
                compact
                description={`Request a refund within ${GUARANTEE_DAYS} days if the bootcamp is not for you.`}
              />
            </div>
          </aside>
          <div className="bootcamp-clarity__pricing-support">
            {hasActivePromo && promoEndsAt ? (
              <div className="bootcamp-clarity__promo">
                <PromoCountdown
                  endsAt={promoEndsAt}
                  label={`${promoTierName || 'Current'} pricing ends in`}
                  units={['days', 'hours']}
                  variant="blocks"
                />
              </div>
            ) : null}
            {showOffer && isEnrollable && selfPaced?.display ? (
              <div className="bootcamp-clarity__alt-path">
                <p>Prefer to learn on your own schedule?</p>
                <Button
                  as={Link}
                  data-testid="pricing-self-paced-link"
                  onClick={() => trackPath('self_paced', 'pricing')}
                  size="sm"
                  to={SELF_PACED_PATH}
                  variant="secondary"
                >
                  Get the self-paced archive for {selfPaced.display}
                </Button>
              </div>
            ) : null}
            {/* Supporting details follow live enrollment in reading order. */}
            <ul className="bootcamp-clarity__reassurance" data-testid="checkout-reassurance">
              <li>Secure checkout through Stripe</li>
              <li>Every session recorded and kept in your portal</li>
              <li>Lifetime access, retake any later cohort</li>
            </ul>
            {priceChange ? (
              <p className="bootcamp-clarity__price-change" data-testid="price-change">
                This price holds until {priceChange.endsOn}. After that the course is{' '}
                {priceChange.nextPrice}.
              </p>
            ) : null}
          </div>
        </div>

        {/* The free way in sits with the price it is an alternative to. As its
            own section it fell between the qualifier and the questions, and
            read as a section the page had nowhere else to put. */}
        <div
          className="bootcamp-clarity__alt-offer"
          aria-labelledby="bootcamp-challenge-title"
          data-testid="challenge-path"
        >
          <div className="control-home__container clarity-home__split">
            <div className="clarity-home__split-copy">
              <h3 id="bootcamp-challenge-title">Try a free live session first.</h3>
              <p>
                Join the free {AI_TRADING_CHALLENGE_NAME}. Bring one trading idea, research it with
                AI, and prepare a trade to review with Rob. Two live sessions, one week apart. No
                coding required.
              </p>
              {nextChallenge && nextChallengeKickoff ? (
                <p data-testid="next-challenge">Next kickoff {nextChallengeKickoff}, Eastern.</p>
              ) : (
                <p>Upcoming free live challenges post here when the next kickoff is scheduled.</p>
              )}
              <div className="control-home__section-actions">
                <Button
                  as={Link}
                  iconAfter={<ArrowRight aria-hidden="true" />}
                  onClick={() => trackChallenge('challenge_primary')}
                  to={CHALLENGES_PATH}
                >
                  Join the free challenge
                </Button>
              </div>
            </div>
            <details className="bootcamp-clarity__challenge-details">
              <summary>What happens during the free week?</summary>
              <aside
                className="clarity-home__learn-slate bootcamp-clarity__slate"
                aria-label="Challenge week"
              >
                <header>
                  <strong>Live with Rob</strong>
                  <span>Free · Seven days · No credit card</span>
                </header>
                <ol>
                  <li>
                    <strong>Day 1: Kickoff with Rob</strong>
                    <span>Choose your trading idea and start researching it.</span>
                  </li>
                  <li>
                    <strong>Days 2 to 6: One idea. Your pace.</strong>
                    <span>Research with AI and prepare a trade to review.</span>
                  </li>
                  <li>
                    <strong>Day 7: Live wrap-up</strong>
                    <span>Review your progress with Rob, even if you are not finished.</span>
                  </li>
                </ol>
              </aside>
            </details>
          </div>
        </div>
      </section>

      <section
        className="control-home__section bootcamp-clarity__faq"
        aria-labelledby="bootcamp-faq-title"
      >
        {/* A header over the questions, not a column beside them: thirteen
            questions ran 961px down the right while the left held a title and
            one line, and the section was most of a screen of nothing. */}
        <div className="control-home__container">
          <header className="clarity-home__chapter">
            <div>
              <h2 id="bootcamp-faq-title">Questions.</h2>
              <p>Anything else? Ask Spot on this page, or talk to Rob before you enroll.</p>
            </div>
          </header>
          <div className="bootcamp-clarity__faq-list">
            {FAQS.map((faq) => (
              <details key={faq.q} className="bootcamp-clarity__details">
                <summary>
                  {faq.q}
                  <ChevronDown aria-hidden="true" size={18} />
                </summary>
                <p>{faq.a}</p>
                {faq.q === 'Can I try it before I enroll?' ? (
                  <Button
                    as={Link}
                    onClick={() => trackChallenge('faq')}
                    size="sm"
                    to={CHALLENGES_PATH}
                    variant="secondary"
                  >
                    Join the free challenge
                  </Button>
                ) : null}
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="control-home__final">
        <div className="control-home__container control-home__final-grid">
          <div>
            <h2>Enroll in the {hero.courseName}.</h2>
            {startLabel ? (
              <p className="bootcamp-clarity__final-detail">Starts {startLabel}.</p>
            ) : null}
          </div>
          <div className="control-home__final-actions">
            <div className="bootcamp-clarity__final-primary" ref={finalActionRef}>
              <Button
                data-testid="enroll-final"
                disabled={ctaDisabled}
                iconAfter={<ArrowRight aria-hidden="true" />}
                onClick={() => enroll('final')}
                size="lg"
                type="button"
              >
                {ctaLabel}
              </Button>
            </div>
            <Button
              as={Link}
              onClick={() => trackChallenge('final')}
              to={CHALLENGES_PATH}
              variant="ghost"
            >
              Join the free challenge
            </Button>
            {selfPaced?.display ? (
              <Button
                as={Link}
                data-testid="final-self-paced-link"
                onClick={() => trackPath('self_paced', 'final')}
                to={SELF_PACED_PATH}
                variant="ghost"
              >
                Self-paced archive
              </Button>
            ) : null}
          </div>
        </div>
        {checkoutRecovery('final')}
      </section>

      {/* The offer follows the reader once the hero action is gone, and only
          while there is a real, purchasable offer to follow them with. */}
      {showStickyBar && selectedPlan ? (
        <div className="bootcamp-clarity__sticky" data-testid="sticky-bar" ref={stickyRef}>
          <div className="bootcamp-clarity__sticky-inner">
            <div className="bootcamp-clarity__sticky-copy">
              <strong>{hero.courseName}</strong>
              <span>
                <span data-testid="sticky-price">
                  {selectedPlan.display}
                  {selectedPlan.installments > 1
                    ? `/mo · ${selectedPlan.totalDisplay} total`
                    : ' once'}
                </span>
              </span>
            </div>
            <Button
              data-testid="enroll-sticky"
              disabled={ctaDisabled}
              iconAfter={<ArrowRight aria-hidden="true" />}
              onClick={() => enroll('sticky')}
              type="button"
            >
              {ctaLabel}
            </Button>
            {checkoutRecovery('sticky')}
          </div>
        </div>
      ) : null}

      <div className="control-home__container bootcamp-clarity__legal">
        <details>
          <summary>
            <span>Educational only. Trading involves risk.</span>
            <span>Full disclaimer</span>
          </summary>
          <CourseLegalDisclaimer />
        </details>
      </div>
      {createPortal(
        <div
          className="bootcamp-clarity-chat"
          data-bs-theme={websiteTheme}
          data-sticky-offer={showStickyBar ? 'true' : undefined}
          ref={chatRef}
        >
          <SpotChatWidget />
        </div>,
        document.body,
      )}
    </div>
  );
}
